/**
 * Classroom identity — class code + anonymous seat handle (CL-145, roadmap H2.1).
 *
 * Teachers share a link like https://earthvoice-v2.pages.dev/?class=ABC234.
 * On startup, initClassroom() captures the code, strips the param from the
 * URL (same hygiene as license.js), and — when the student isn't already a
 * member of a class — shows a join dialog where they pick an anonymous
 * handle ("amber-fox-42" style). Zero PII by design (COPPA posture: the
 * school is the customer, students stay anonymous): the Worker rejects
 * email- and phone-shaped handles, and nothing else about the student is
 * ever sent or stored.
 *
 * Joining adopts the class's school license key into 'ev:license' (the key
 * src/utils/license.js reads), so students on personal devices get the
 * school's licensed rate-limit tier. Membership is persisted under
 * 'ev:class' + 'ev:handle' and sent as X-EV-Class / X-EV-Handle headers on
 * /api/intent, /api/stream, and /api/grade — telemetry attribution lands
 * in CL-146.
 */
import { createLogger } from './logger.js'

const log = createLogger('classroom')

export const CLASS_STORAGE_KEY = 'ev:class'
export const HANDLE_STORAGE_KEY = 'ev:handle'
// Same key src/utils/license.js reads (CL-131) — adopted on join.
const LICENSE_STORAGE_KEY = 'ev:license'

const API_URL = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) || ''

// Kid-friendly handle suggestions: adjective-animal-2digits. Words are kept
// short so the longest combination (9+1+9+1+2 = 22 chars) stays inside the
// Worker's 3-24 char handle window.
const HANDLE_ADJECTIVES = [
  'amber',
  'brave',
  'clever',
  'cosmic',
  'dusty',
  'gentle',
  'golden',
  'happy',
  'lucky',
  'mighty',
  'noble',
  'polar',
  'quiet',
  'rapid',
  'sandy',
  'silver',
  'sunny',
  'swift',
  'tidal',
  'wild',
] // longest: 6 chars
const HANDLE_ANIMALS = [
  'badger',
  'bison',
  'condor',
  'dolphin',
  'falcon',
  'fox',
  'gecko',
  'heron',
  'ibex',
  'jaguar',
  'koala',
  'lemur',
  'lynx',
  'marmot',
  'narwhal',
  'otter',
  'panda',
  'puffin',
  'toucan',
  'wombat',
] // longest: 7 chars

/**
 * Random "amber-fox-42" style handle suggestion. `rand` is injectable for
 * deterministic tests (tests/classroom.test.js).
 */
export function suggestHandle(rand = Math.random) {
  const adjective = HANDLE_ADJECTIVES[Math.floor(rand() * HANDLE_ADJECTIVES.length)]
  const animal = HANDLE_ANIMALS[Math.floor(rand() * HANDLE_ANIMALS.length)]
  const digits = String(10 + Math.floor(rand() * 90)) // 10-99, always 2 digits
  return `${adjective}-${animal}-${digits}`
}

function safeStorage() {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null
  } catch {
    // localStorage access can throw in sandboxed contexts
    return null
  }
}

/** Stored membership, or null when not joined: { classCode, handle }. */
export function getClassroom() {
  const storage = safeStorage()
  if (!storage) return null
  try {
    const classCode = storage.getItem(CLASS_STORAGE_KEY)
    const handle = storage.getItem(HANDLE_STORAGE_KEY)
    return classCode && handle ? { classCode, handle } : null
  } catch {
    return null
  }
}

/**
 * Headers to spread into API fetches (same pattern as licenseHeaders):
 * {} when not joined, or { 'X-EV-Class', 'X-EV-Handle' } when joined.
 */
export function classroomHeaders() {
  const classroom = getClassroom()
  return classroom ? { 'X-EV-Class': classroom.classCode, 'X-EV-Handle': classroom.handle } : {}
}

/**
 * Persist a successful join: membership + the class's school license key
 * (adopted into the key license.js reads, so licenseHeaders() picks it up
 * on the next API call). Exported for tests. Fail-soft: storage errors
 * only mean the student re-joins next visit.
 */
export function storeMembership(classCode, handle, licenseKey) {
  const storage = safeStorage()
  if (!storage) return
  try {
    storage.setItem(CLASS_STORAGE_KEY, classCode)
    storage.setItem(HANDLE_STORAGE_KEY, handle)
    if (licenseKey) storage.setItem(LICENSE_STORAGE_KEY, licenseKey)
  } catch (err) {
    log.warn('could not persist class membership', err)
  }
}

/**
 * Read ?class=... from the URL and strip it from the address bar (same
 * mechanics as initLicense — the code shouldn't linger in bookmarks or
 * screenshots). Returns the captured code or null. Safe in non-browser
 * contexts.
 */
function captureClassParam() {
  if (typeof window === 'undefined') return null
  try {
    const url = new URL(window.location.href)
    const code = (url.searchParams.get('class') || '').trim().toUpperCase()
    if (!code) return null
    url.searchParams.delete('class')
    window.history.replaceState(null, '', url.pathname + url.search + url.hash)
    return code
  } catch (err) {
    log.warn('class param capture failed', err)
    return null
  }
}

/**
 * Call once from main.js after initLicense(). Captures + strips the ?class=
 * param; when a code is present and the student has no stored membership,
 * shows the join dialog. Already-joined students keep their seat (rejoining
 * a different class is a teacher-guided flow: clear site data or a fresh
 * profile — deliberate for v1).
 */
export function initClassroom() {
  const code = captureClassParam()
  if (!code) return
  if (getClassroom()) {
    log.info('class link ignored — already joined a class')
    return
  }
  if (typeof document === 'undefined') return
  showJoinDialog(code)
}

// ── Join dialog (mic-consent modal pattern, CL-135) ──────────
// All user-visible text is static strings or server-validated values
// assigned via textContent (XSS TIER 3 rule — nothing goes through
// innerHTML).

function showJoinDialog(classCode) {
  if (document.querySelector('.classroom-join-overlay')) return

  const overlay = document.createElement('div')
  overlay.className = 'classroom-join-overlay'

  const modal = document.createElement('div')
  modal.className = 'classroom-join-modal'
  modal.setAttribute('role', 'dialog')
  modal.setAttribute('aria-modal', 'true')
  modal.setAttribute('aria-labelledby', 'classroom-join-title')

  const title = document.createElement('h2')
  title.className = 'classroom-join-title'
  title.id = 'classroom-join-title'
  title.textContent = 'Join your class'

  const codeLine = document.createElement('p')
  codeLine.className = 'classroom-join-code'
  codeLine.textContent = `Class code: ${classCode}`

  const guidance = document.createElement('p')
  guidance.className = 'classroom-join-guidance'
  guidance.textContent = "Pick a fun name — don't use your real name!"

  const inputRow = document.createElement('div')
  inputRow.className = 'classroom-join-input-row'

  const input = document.createElement('input')
  input.type = 'text'
  input.className = 'classroom-join-input'
  input.placeholder = 'amber-fox-42'
  input.maxLength = 24
  input.autocomplete = 'off'
  input.spellcheck = false
  input.value = suggestHandle()

  const suggestBtn = document.createElement('button')
  suggestBtn.type = 'button'
  suggestBtn.className = 'classroom-join-suggest'
  suggestBtn.title = 'Suggest another name'
  suggestBtn.setAttribute('aria-label', 'Suggest another name')
  suggestBtn.textContent = '\u{1F3B2}' // 🎲

  inputRow.appendChild(input)
  inputRow.appendChild(suggestBtn)

  const status = document.createElement('p')
  status.className = 'classroom-join-status'
  status.setAttribute('role', 'status')

  const actions = document.createElement('div')
  actions.className = 'classroom-join-actions'

  const dismissBtn = document.createElement('button')
  dismissBtn.type = 'button'
  dismissBtn.className = 'classroom-join-dismiss'
  dismissBtn.textContent = 'Not now'

  const joinBtn = document.createElement('button')
  joinBtn.type = 'button'
  joinBtn.className = 'classroom-join-accept'
  joinBtn.textContent = 'Join class'

  actions.appendChild(dismissBtn)
  actions.appendChild(joinBtn)
  modal.appendChild(title)
  modal.appendChild(codeLine)
  modal.appendChild(guidance)
  modal.appendChild(inputRow)
  modal.appendChild(status)
  modal.appendChild(actions)
  overlay.appendChild(modal)

  function close() {
    document.removeEventListener('keydown', onKeydown, true)
    overlay.remove()
  }

  // Capture-phase so Escape closes the modal instead of triggering the
  // app-wide Escape=reset binding in ui/overlay.js, and so Space types in
  // the input / activates buttons instead of toggling voice globally.
  function onKeydown(e) {
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      close()
    } else if (e.code === 'Space') {
      e.stopPropagation()
    } else if (e.key === 'Enter' && e.target === input) {
      e.stopPropagation()
      joinBtn.click()
    }
  }

  async function join() {
    const handle = input.value.trim().toLowerCase()
    if (!handle) {
      status.textContent = 'Pick a name first!'
      return
    }
    joinBtn.disabled = true
    status.textContent = 'Joining…'
    try {
      const resp = await fetch(`${API_URL}/api/classes/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ class_code: classCode, handle }),
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        // Worker error strings are our own static messages, but they still
        // arrive over the wire — textContent keeps this TIER 3 safe.
        status.textContent = data.error || `Could not join (HTTP ${resp.status})`
        joinBtn.disabled = false
        return
      }
      storeMembership(data.class_code || classCode, data.handle || handle, data.license_key)
      log.info('joined class')
      // Class name comes from the server after code validation — show the
      // confirmation, then let the student get on with exploring.
      title.textContent = `Welcome to ${data.class_name || 'your class'}!`
      codeLine.textContent = `You're in as ${data.handle || handle}.`
      guidance.remove()
      inputRow.remove()
      status.textContent = ''
      dismissBtn.remove()
      joinBtn.disabled = false
      joinBtn.textContent = 'Start exploring'
      joinBtn.onclick = () => close()
      joinBtn.focus()
    } catch (err) {
      log.warn('class join failed', err)
      status.textContent = 'Could not reach the server — try again in a minute.'
      joinBtn.disabled = false
    }
  }

  suggestBtn.addEventListener('click', () => {
    input.value = suggestHandle()
    status.textContent = ''
    input.focus()
  })
  joinBtn.addEventListener('click', () => {
    if (joinBtn.textContent === 'Join class') join()
  })
  dismissBtn.addEventListener('click', () => close())
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close()
  })
  document.addEventListener('keydown', onKeydown, true)

  document.body.appendChild(overlay)
  input.focus()
  input.select()
}
